// Browser-side wrapper around the SSR server's /api/training/* proxy. The
// real CanXP training API key never leaves the server; the browser just
// hits same-origin routes that pass through with the Supabase session
// cookie.

import type {
  JobArtifact,
  JobEvent,
  JobList,
  JobSpec,
  SupportedModel,
  TrainingJob,
  TrainingMetricsResponse,
} from '@/types/training';

function previewResponseText(text: string, maxLength = 180) {
  const normalized = text.replace(/\s+/g, ' ').trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}…` : normalized;
}

async function parseJsonResponse<T>(response: Response, path: string): Promise<T> {
  const text = await response.text().catch(() => '');
  const contentType = response.headers.get('content-type') || '';
  const looksJson = contentType.includes('application/json') || /^\s*[\[{]/.test(text);

  if (!response.ok) {
    if (looksJson && text.trim()) {
      try {
        const parsed = JSON.parse(text) as { error?: string; detail?: string; message?: string };
        const detail = parsed.detail || parsed.error || parsed.message;
        throw new Error(`HTTP ${response.status} ${response.statusText}: ${detail || path}`);
      } catch {
        // Fall through to the plain-text error below when the body isn't valid JSON.
      }
    }

    throw new Error(`HTTP ${response.status} ${response.statusText}: ${previewResponseText(text) || path}`);
  }

  if (!text.trim()) {
    throw new Error(`Empty JSON response from ${path}`);
  }

  if (!looksJson) {
    throw new Error(`Expected JSON from ${path} but received ${previewResponseText(text) || contentType || 'non-JSON content'}`);
  }

  try {
    return JSON.parse(text) as T;
  } catch (error: any) {
    throw new Error(`Invalid JSON from ${path}: ${previewResponseText(text) || error?.message || 'parse failed'}`);
  }
}

async function jsonFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
    credentials: 'include',
  });

  return parseJsonResponse<T>(response, path);
}

export const TrainingService = {
  listSupportedModels(): Promise<SupportedModel[]> {
    return jsonFetch('/api/training/models');
  },

  listJobs(opts: { status?: string; limit?: number; offset?: number } = {}): Promise<JobList> {
    const q = new URLSearchParams();
    if (opts.status) q.set('status', opts.status);
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return jsonFetch(`/api/training/jobs${qs ? `?${qs}` : ''}`);
  },

  getJob(jobId: string): Promise<TrainingJob> {
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}`);
  },

  createJob(spec: JobSpec): Promise<TrainingJob> {
    return jsonFetch('/api/training/jobs', {
      method: 'POST',
      body: JSON.stringify(spec),
    });
  },

  cancelJob(jobId: string): Promise<TrainingJob> {
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    });
  },

  listEvents(jobId: string, after?: number, limit = 200): Promise<JobEvent[]> {
    const q = new URLSearchParams();
    if (after !== undefined) q.set('after', String(after));
    q.set('limit', String(limit));
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}/events?${q.toString()}`);
  },

  async getMetrics(
    jobId: string,
    opts: { afterSeq?: number; etag?: string; signal?: AbortSignal } = {},
  ): Promise<{ status: 200 | 304; data: TrainingMetricsResponse | null; etag: string | null }> {
    const q = new URLSearchParams();
    if (opts.afterSeq !== undefined) q.set('after_seq', String(opts.afterSeq));
    const qs = q.toString();
    const path = `/api/training/jobs/${encodeURIComponent(jobId)}/metrics${qs ? `?${qs}` : ''}`;
    const response = await fetch(path, {
      headers: {
        Accept: 'application/json',
        ...(opts.etag ? { 'If-None-Match': opts.etag } : {}),
      },
      credentials: 'include',
      signal: opts.signal,
    });

    if (response.status === 304) {
      return {
        status: 304,
        data: null,
        etag: response.headers.get('ETag'),
      };
    }

    return {
      status: 200,
      data: await parseJsonResponse<TrainingMetricsResponse>(response, path),
      etag: response.headers.get('ETag'),
    };
  },

  listArtifacts(jobId: string): Promise<JobArtifact[]> {
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}/artifacts`);
  },

  /**
   * URL the browser can open / a link can point at to download the
   * artifact bytes. The SSR server streams from the training backend
   * with the API key attached.
   */
  artifactDownloadUrl(jobId: string, artifactId: string): string {
    return `/api/training/jobs/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(artifactId)}/download`;
  },

  /**
   * Submit a chat request. Returns immediately with a request_id; the
   * browser polls chatStatus() until the inference completes. We use
   * submit + poll (rather than holding one HTTP connection open) so a
   * RunPod cold-image-pull (up to 15 min for our 18.5 GB image) can't
   * be killed by any intermediary timeout.
   */
  submitChat(
    jobId: string,
    body: {
      messages: { role: string; content: string }[];
      max_tokens?: number;
      temperature?: number;
      top_p?: number;
      stop?: string[];
      // Anti-repetition. Default 1.15 on the backend; raise to 1.3 if
      // outputs still loop. The proxy / pod accept the float as-is.
      repetition_penalty?: number;
      frequency_penalty?: number;
      presence_penalty?: number;
    },
  ): Promise<{ request_id: string; status: string }> {
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}/chat`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Ask the backend to make sure an inference pod is allocated + healthy
   * for this job's model. Returns the current readiness state so the
   * dashboard can show a meaningful status while we wait.
   */
  prepareChat(jobId: string): Promise<{
    status: 'starting' | 'pulling_image' | 'loading_model' | 'ready' | 'failed';
    message: string;
    pod_id?: string;
    elapsed_seconds?: number;
  }> {
    return jsonFetch(`/api/training/jobs/${encodeURIComponent(jobId)}/chat/prepare`, {
      method: 'POST',
      body: '{}',
    });
  },

  /**
   * Flip a completed fine-tune to/from Hosted mode. Gating happens at
   * the SSR proxy:
   *  - Admins get through for free
   *  - Users with an active dedicated-inference-endpoint subscription
   *    get through (no extra charge — they already pay $29/mo)
   *  - Anyone else gets HTTP 402 with `{ checkout_required: true,
   *    addon_id: 'dedicated-inference-endpoint' }` and the caller is
   *    expected to kick the user into Stripe Checkout
   *
   * Throws an Error with `.checkoutRequired === true` and `.addonId` on
   * the 402 path so the caller can branch cleanly.
   */
  async setHosted(jobId: string, enable: boolean): Promise<{ is_hosted: boolean; _gate?: 'admin' | 'subscription' }> {
    const resp = await fetch(
      `/api/training/jobs/${encodeURIComponent(jobId)}/host`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable }),
      },
    );
    if (resp.status === 402) {
      const body = await resp.json().catch(() => ({}));
      const err: any = new Error(body?.message || 'subscription required');
      err.checkoutRequired = true;
      err.addonId = body?.addon_id || 'dedicated-inference-endpoint';
      throw err;
    }
    if (!resp.ok) {
      const detail = await resp.text();
      throw new Error(`host HTTP ${resp.status}: ${detail.slice(0, 200)}`);
    }
    return resp.json();
  },

  /**
   * Issue or rotate the per-job API key. Returns plaintext exactly
   * once — store it, then. The base_url is the value the user should
   * pass to their OpenAI SDK's `base_url` constructor argument; their
   * SDK will append `/chat/completions` to it automatically.
   *
   * Throws on 402 with `.checkoutRequired === true` same as setHosted,
   * because issuing a key requires the same Stripe subscription gate.
   */
  async issueApiKey(jobId: string): Promise<{
    api_key: string;
    created_at: string;
    job_id: string;
    base_url: string;
  }> {
    const resp = await fetch(
      `/api/training/jobs/${encodeURIComponent(jobId)}/api_key`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    if (resp.status === 402) {
      const body = await resp.json().catch(() => ({}));
      const err: any = new Error(body?.message || 'subscription required');
      err.checkoutRequired = true;
      err.addonId = body?.addon_id || 'dedicated-inference-endpoint';
      throw err;
    }
    if (!resp.ok) {
      throw new Error(`api_key HTTP ${resp.status}: ${(await resp.text()).slice(0, 200)}`);
    }
    return resp.json();
  },

  /**
   * Revoke the current per-job API key. Subsequent calls with the old
   * plaintext will return 401. Re-issue via issueApiKey() to mint a
   * new one.
   */
  async revokeApiKey(jobId: string): Promise<void> {
    const resp = await fetch(
      `/api/training/jobs/${encodeURIComponent(jobId)}/api_key`,
      { method: 'DELETE' },
    );
    if (!resp.ok) {
      throw new Error(`revoke HTTP ${resp.status}: ${(await resp.text()).slice(0, 200)}`);
    }
  },

  /**
   * Set (or clear with null/empty) the customer's system prompt for
   * this job. Applies to chat AND OpenAI passthrough. The CanXP brand
   * preamble is prepended server-side; this method just sends the
   * customer's persona/scope content.
   */
  async setSystemPrompt(jobId: string, prompt: string | null): Promise<TrainingJob> {
    const resp = await fetch(
      `/api/training/jobs/${encodeURIComponent(jobId)}/system_prompt`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system_prompt: prompt ?? null }),
      },
    );
    if (!resp.ok) {
      throw new Error(`system_prompt HTTP ${resp.status}: ${(await resp.text()).slice(0, 200)}`);
    }
    return resp.json();
  },

  chatStatus(requestId: string): Promise<{
    request_id: string;
    status: 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'TIMED_OUT' | 'UNKNOWN';
    output?: {
      choices: { message: { role: string; content: string }; finish_reason?: string }[];
      usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
      model?: string;
      _meta?: { elapsed_seconds?: number; adapter_repo?: string; base_model?: string };
    };
    error?: string;
  }> {
    return jsonFetch(`/api/training/chat/${encodeURIComponent(requestId)}`);
  },

  /**
   * Streaming variant of submitChat. Opens an SSE connection to the
   * backend and invokes onDelta() with each token's content as it
   * arrives. The promise resolves once the stream ends ([DONE] event
   * received) or rejects on transport error. Pass an AbortSignal to
   * cancel mid-flight if the user navigates away or starts a new turn.
   *
   * vLLM's SSE chunks are OpenAI chat-completion delta shape:
   *   data: {"choices":[{"delta":{"content":"hel"},"index":0}], ...}
   *   data: {"choices":[{"delta":{"content":"lo"},  "index":0}], ...}
   *   data: [DONE]
   */
  async streamChat(
    jobId: string,
    body: {
      messages: { role: string; content: string }[];
      max_tokens?: number;
      temperature?: number;
      top_p?: number;
      stop?: string[];
      repetition_penalty?: number;
      frequency_penalty?: number;
      presence_penalty?: number;
    },
    onDelta: (textDelta: string) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const resp = await fetch(
      `/api/training/jobs/${encodeURIComponent(jobId)}/chat/stream`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (!resp.ok) {
      let detail = '';
      try { detail = (await resp.json())?.error || (await resp.text()); }
      catch { /* swallow */ }
      throw new Error(`chat/stream HTTP ${resp.status}${detail ? `: ${detail}` : ''}`);
    }
    if (!resp.body) throw new Error('chat/stream: empty response body');

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buf = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE events are separated by a blank line. Parse all complete
      // events out of the buffer; the trailing partial event (if any)
      // stays buffered until more bytes arrive.
      let sepIdx: number;
      while ((sepIdx = buf.indexOf('\n\n')) >= 0) {
        const rawEvent = buf.slice(0, sepIdx);
        buf = buf.slice(sepIdx + 2);
        // Each event is one or more `data: ...` lines; we only care
        // about `data:` and skip anything else (`event:`, `id:`,
        // comments, etc.).
        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          if (payload === '[DONE]') return;
          try {
            const obj = JSON.parse(payload);
            if (obj.error) {
              throw new Error(String(obj.error));
            }
            const delta = obj?.choices?.[0]?.delta?.content;
            if (typeof delta === 'string' && delta.length) {
              onDelta(delta);
            }
          } catch (e: any) {
            // Surface unexpected JSON parse failures; keep going on
            // malformed event lines so one bad chunk doesn't tear
            // down the whole stream.
            if (e?.message?.startsWith('Unexpected token')) continue;
            throw e;
          }
        }
      }
    }
  },
};
